import type { TileQueryRequest, TileResult } from "@csbi/contracts"
import { ApiError } from "../../shared/api/request.js"
import { queryTile, queryTiles } from "./api.js"

const WINDOW_MS = 10

const MAX_PER_BATCH = 24

type Waiting = {
  request: TileQueryRequest
  signal: AbortSignal | undefined
  resolve: (result: TileResult) => void
  reject: (err: unknown) => void
}

let queue: Waiting[] = []
let timer: ReturnType<typeof setTimeout> | null = null

export function queryTileBatched(request: TileQueryRequest, signal?: AbortSignal): Promise<TileResult> {
  return new Promise<TileResult>((resolve, reject) => {
    queue.push({ request, signal, resolve, reject })
    if (timer === null) timer = setTimeout(flush, WINDOW_MS)
  })
}

function flush() {
  timer = null
  const pending = queue
  queue = []
  for (let i = 0; i < pending.length; i += MAX_PER_BATCH) {
    void send(pending.slice(i, i + MAX_PER_BATCH))
  }
}

async function send(group: Waiting[]): Promise<void> {
  const live: Waiting[] = []
  for (const w of group) {
    if (w.signal?.aborted) w.reject(abortError())
    else live.push(w)
  }
  if (live.length === 0) return

  /**
   * ⚠ **The request is only cancelled when every participant has withdrawn.**
   */
  const controller = new AbortController()
  let waiting = live.length
  const onAbort = () => {
    if (--waiting === 0) controller.abort()
  }
  for (const w of live) w.signal?.addEventListener("abort", onAbort, { once: true })
  const cleanup = () => {
    for (const w of live) w.signal?.removeEventListener("abort", onAbort)
  }

  try {
    if (live.length === 1) {
      const only = live[0]!
      only.resolve(await queryTile(only.request, controller.signal))
      return
    }

    const { results } = await queryTiles(
      live.map((w) => w.request),
      controller.signal,
    )
    live.forEach((w, i) => {
      // Matched back **by index**: tileKeys can repeat (the same metric on two charts), and dispatching by name would cross the wires
      const item = results[i]
      if (item === undefined) {
        w.reject(new Error("Batch response is missing this tile’s entry."))
      } else if (item.ok) {
        w.resolve(item.result)
      } else {
        // Restored to a faithful ApiError — the retry policy decides on status (403 must not retry, 500 should)
        w.reject(new ApiError(item.status, item.error))
      }
    })
  } catch (err) {
    for (const w of live) w.reject(err)
  } finally {
    cleanup()
  }
}

const abortError = () => new DOMException("Aborted", "AbortError")
